'use client';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';

interface Order {
  id: string;
  customerName: string;
  dateTime: string;
  products: string[];
  subtotal: string;
  sourceCampaign: string;
}

// Sample orders data - in a real app, this would be fetched from an API
const allOrders: Order[] = [
  {
    id: 'ORD-7829',
    customerName: 'Alex Johnson',
    dateTime: '2023-06-12 14:32',
    products: ['Premium Vape Pen', 'CBD Tincture'],
    subtotal: '$78.50',
    sourceCampaign: 'New Product Launch',
  },
  {
    id: 'ORD-7830',
    customerName: 'Morgan Smith',
    dateTime: '2023-06-12 15:47',
    products: ['Indica Gummies', 'Rolling Papers'],
    subtotal: '$42.25',
    sourceCampaign: 'New Product Launch',
  },
  {
    id: 'ORD-7836',
    customerName: 'Jamie Williams',
    dateTime: '2023-06-12 16:23',
    products: ['Sativa Pre-Roll Pack', 'Grinder'],
    subtotal: '$65.75',
    sourceCampaign: 'Flash Sale Announcement',
  },
  {
    id: 'ORD-7842',
    customerName: 'Taylor Brown',
    dateTime: '2023-06-12 17:05',
    products: ['CBD Bath Bomb', 'Hemp Lotion'],
    subtotal: '$54.50',
    sourceCampaign: 'Flash Sale Announcement',
  },
  {
    id: 'ORD-7851',
    customerName: 'Casey Davis',
    dateTime: '2023-06-12 18:12',
    products: ['Hybrid Flower 3.5g', 'Rolling Tray'],
    subtotal: '$87.25',
    sourceCampaign: 'Flash Sale Announcement',
  },
  {
    id: 'ORD-7855',
    customerName: 'Jordan Miller',
    dateTime: '2023-06-13 09:34',
    products: ['Disposable Vape', 'Odor Eliminator Spray'],
    subtotal: '$63.75',
    sourceCampaign: 'Monthly Newsletter',
  },
  {
    id: 'ORD-7861',
    customerName: 'Riley Wilson',
    dateTime: '2023-06-13 10:47',
    products: ['CBD Topical Cream', 'THC Tincture'],
    subtotal: '$92.50',
    sourceCampaign: 'Loyalty Program Update',
  },
  {
    id: 'ORD-7868',
    customerName: 'Avery Thomas',
    dateTime: '2023-06-13 11:23',
    products: ['Indica Flower 7g', 'Glass Pipe'],
    subtotal: '$124.75',
    sourceCampaign: 'Loyalty Program Update',
  },
  {
    id: 'ORD-7869',
    customerName: 'Sam Parker',
    dateTime: '2023-06-13 12:15',
    products: ['Sativa Gummies', 'CBD Oil'],
    subtotal: '$68.25',
    sourceCampaign: 'Flash Sale Alert',
  },
  {
    id: 'ORD-7872',
    customerName: 'Jesse Morgan',
    dateTime: '2023-06-13 13:42',
    products: ['Hybrid Pre-Roll Pack', 'Rolling Papers'],
    subtotal: '$45.50',
    sourceCampaign: 'Flash Sale Alert',
  },
  {
    id: 'ORD-7875',
    customerName: 'Cameron Lee',
    dateTime: '2023-06-13 14:23',
    products: ['CBD Topical Cream', 'Indica Flower 3.5g', 'Glass Pipe'],
    subtotal: '$112.75',
    sourceCampaign: 'Flash Sale Alert',
  },
  {
    id: 'ORD-7878',
    customerName: 'Taylor Reed',
    dateTime: '2023-06-13 15:07',
    products: ['Disposable Vape', 'Hybrid Gummies'],
    subtotal: '$58.25',
    sourceCampaign: 'Flash Sale Alert',
  },
  {
    id: 'ORD-7881',
    customerName: 'Jordan Quinn',
    dateTime: '2023-06-13 16:18',
    products: ['Sativa Flower 7g', 'Rolling Tray', 'Grinder'],
    subtotal: '$97.50',
    sourceCampaign: 'Flash Sale Alert',
  },
];

interface OrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignName: string;
  revenue: string;
}

export function OrdersModal({
  isOpen,
  onClose,
  campaignName,
  revenue,
}: OrdersModalProps) {
  // Filter orders by campaign name
  const filteredOrders = allOrders.filter(
    order => order.sourceCampaign === campaignName,
  );
  const hasRevenue = revenue !== '$0';

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className='sm:max-w-[800px] p-0 overflow-hidden [&>button]:hidden'>
        <DialogHeader className='p-6 pb-4 border-b'>
          <div className='flex items-center justify-between'>
            <DialogTitle className='text-xl font-semibold'>
              Campaign Revenue Details
            </DialogTitle>
            <Button
              variant='ghost'
              size='icon'
              onClick={onClose}
              className='h-8 w-8'
              aria-label='Close dialog'
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
          <div className='flex flex-col gap-2 mt-4 text-left'>
            <div>
              <span className='text-md text-muted-foreground'>Campaign:</span>
              <span className='ml-2 font-medium'>{campaignName}</span>
            </div>
            <div>
              <span className='text-md text-muted-foreground'>
                Total Revenue:
              </span>
              <span className='ml-2 font-medium text-secondary'>{revenue}</span>
            </div>
          </div>
        </DialogHeader>

        <div className='p-0'>
          {hasRevenue ? (
            <div className='relative'>
              <div className='overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent -webkit-overflow-scrolling-touch'>
                <div className='min-w-[700px]'>
                  <Table>
                    <TableHeader className='bg-muted/30 sticky top-0 z-10'>
                      <TableRow className='hover:bg-transparent border-b border-gray-200'>
                        <TableHead className='w-[100px] bg-white sticky left-0 z-20 font-medium text-sm uppercase tracking-wider'>
                          Order ID
                        </TableHead>
                        <TableHead className='font-medium text-sm uppercase tracking-wider'>
                          Customer Name
                        </TableHead>
                        <TableHead className='font-medium text-sm uppercase tracking-wider'>
                          Date
                        </TableHead>
                        <TableHead className='font-medium text-sm uppercase tracking-wider'>
                          Product(s)
                        </TableHead>
                        <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                          Subtotal
                        </TableHead>
                        <TableHead className='font-medium text-sm uppercase tracking-wider'>
                          Source Campaign
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOrders.map(order => (
                        <TableRow
                          key={order.id}
                          className='hover:bg-muted/10 transition-colors border-b border-gray-100 text-md'
                        >
                          <TableCell className='font-medium bg-white sticky left-0 z-10 !border-0'>
                            {order.id}
                          </TableCell>

                          <TableCell className='max-w-[150px] truncate !border-0'>
                            {order.customerName}
                          </TableCell>

                          <TableCell className='!border-0'>
                            {order.dateTime}
                          </TableCell>
                          <TableCell className='!border-0'>
                            <div className='max-w-[200px] sm:max-w-none'>
                              {order.products.length > 1 ? (
                                <ul className='list-disc pl-4 space-y-1'>
                                  {order.products.map((product, index) => (
                                    <li key={index} className='text-md'>
                                      {product}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span>{order.products[0]}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className='text-right font-medium !border-0'>
                            {order.subtotal}
                          </TableCell>
                          <TableCell className='max-w-[150px] truncate !border-0'>
                            {order.sourceCampaign}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              {/* Gradient shadows for horizontal scrolling indication */}
              <div className='absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none z-20 hidden sm:block'></div>
              <div className='absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none z-20 hidden sm:block'></div>
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center py-12 px-6 text-center'>
              <div className='rounded-full bg-muted p-3 mb-4'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='text-muted-foreground'
                >
                  <rect width='20' height='14' x='2' y='5' rx='2' />
                  <line x1='2' x2='22' y1='10' y2='10' />
                </svg>
              </div>
              <h3 className='text-lg font-medium'>
                No orders attributed to this campaign yet
              </h3>
              <p className='text-md text-muted-foreground mt-1 mb-6'>
                Orders will appear here when customers make purchases through
                this campaign.
              </p>
            </div>
          )}
        </div>

        <div className='p-4 sm:p-6 border-t mt-auto'>
          <Button
            variant='outline'
            onClick={onClose}
            className='w-full sm:w-auto h-10'
          >
            Back to Campaigns
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
